import { useState, useCallback, useEffect, useRef } from 'react';
import { accountService } from '@/features/card/services/accountService/accountService';
import { cardService } from '@/features/card/services/cardService/cardService';
import { metadataUpdateService } from '@/features/card/services/metadataUpdateService/metadataUpdateService';
import { tagManagerService } from '@/features/card/services/tagManagerService/tagManagerService';
import { pinService } from '@/features/card/services/cardService/pinService';
import type { UseCardDetailReturn } from '@/features/card/types/card.types';
import type { AccountData, AccountView } from '@/features/card/types/account.types';
import { STATUS_TRANSACTION, TYPE_TRANSACTION } from '@/features/dashboard/constants/dashboard.constants';
import { formatCurrency, formatDateExact, formatDateTimeExact } from '@chikara/utils';
import { getEnvConfig } from '@/config/env';
import { Card } from '@/features/card/types/card.types';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { useSpinner, useToast } from '@chikara/ui';
import { useAsyncGuard } from '@chikara/hooks';
import { t } from '@/i18n';

export const useCardDetail = (uuid: string | undefined, row: any): UseCardDetailReturn => {
    const { success } = useToast();
    const { show, hide } = useSpinner();
    const { payload: authPayload } = useAuthContext();
    const [activityData, setActivityData] = useState<any[]>([]);
    const [accountData, setAccountData] = useState<AccountData | null>(null);
    const [card, setCard] = useState<Card | null>(null);
    const [viewPermissions, setViewPermissions] = useState<AccountView | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [paginationData, setPaginationData] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });

    const { checkGuard, resetGuard } = useAsyncGuard();

    // Refs to keep functions stable and avoid identity loops
    const accountRef = useRef(row);
    const authRef = useRef(authPayload);

    useEffect(() => {
        authRef.current = authPayload;
    }, [authPayload]);

    const [isTemporaryBlocked, setIsTemporaryBlocked] = useState(false);
    const [isBlockedByAdmin, setIsBlockedByAdmin] = useState(false);
    const [nipValue, setNipValue] = useState('');
    const [showNip, setShowNip] = useState(false);
    const [isChangeNipModalOpen, setIsChangeNipModalOpen] = useState(false);
    const [isEditAliasModalOpen, setIsEditAliasModalOpen] = useState(false);
    const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
    const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
    const [isManageTagsModalOpen, setIsManageTagsModalOpen] = useState(false);
    const [isEditTagModalOpen, setIsEditTagModalOpen] = useState(false);
    const [editingTag, setEditingTag] = useState<string | undefined>();
    const [aliasValue, setAliasValue] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [selectedTag, setSelectedTag] = useState<string | undefined>();
    const [isManageLimitModalOpen, setIsManageLimitModalOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const tagButtonRef = useRef<HTMLDivElement>(null);

    const formatMXN = (amount: number) => formatCurrency(amount, { currency: 'MXN', locale: 'es-MX' });

    const updateAccount = useCallback((newAccount: any) => {
        const value = typeof newAccount === 'function' ? newAccount(accountRef.current) : newAccount;
        accountRef.current = value;
    }, []);

    const handleSetTransactions = useCallback((transactions: any) => {
        const tx = transactions?.tx?.map((tx: any) => {
            return {
                uuid: tx.uuid,
                amount: tx.amount,
                email: tx?.user?.email,
                movement: {
                    description: tx.description || "No aplica",
                    subcategory: `${tx?.card?.pan_masked?.slice(-4) || "----"} / Realizado por ${tx?.user?.fullname || "No aplica"}`,
                },
                type: tx.type && TYPE_TRANSACTION[tx.type] || tx.type,
                status: tx.status,
                report: tx.status === 'settled',
                statusName: tx.status && STATUS_TRANSACTION[tx.status] || tx.status,
                cdate: formatDateTimeExact(tx.cdate),
                tracking_key: tx.tracking_key || "",
                narrative: tx.remote_account.narrative || "",
            }
        })
        setPaginationData((prev) => ({
            ...prev,
            totalItems: transactions.total_documents,
            totalPages: transactions.total_pages
        }));
        tx && setActivityData(tx);
    }, []);

    const getTransactions = useCallback(async (page: number, limit: number, setResult: boolean = false) => {
        if (!uuid) return;

        show();
        setError(null);
        const genericFilters: Record<string, any>[] = [];
        try {
            const response = await accountService.getAccountTransactions({
                account: { uuid: uuid },
                filter: {
                    results_per_page: limit,
                    page_number: page,
                    ...(genericFilters.length > 0 && { genericFilters })
                }
            })
            if (setResult) {
                return handleSetTransactions(response.result.tx);
            }
            return response;
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, show, hide, handleSetTransactions]);

    const fetchCard = useCallback(async () => {
        if (!uuid || !checkGuard()) return;

        show();
        setError(null);
        try {
            const currentAccount = accountRef.current;
            const currentAuth = authRef.current;
            const sameUser = currentAuth?.user_uuid === currentAccount.user.uuid;

            const request = await Promise.all([
                getTransactions(paginationData.currentPage, paginationData.itemsPerPage),
                currentAccount.card_uuid && currentAccount.card_status === 'enabled' && sameUser && pinService.getCardPin({
                    account: uuid,
                    card_uuid: currentAccount.card_uuid
                })
            ]);

            request[0]?.result && handleSetTransactions(request[0]?.result);

            if (request[1]?.card?.pin) {
                setNipValue(request[1]?.card?.pin);
            }

            setCard({
                uuid: currentAccount.uuid,
                disposable: currentAccount.balance?.disposable,
                disposableParse: formatMXN(currentAccount.balance?.disposable),
                card: `${currentAccount.cardAlias} / ${currentAccount.maskedPan}`,
                cardAlias: currentAccount.cardAlias,
                card_uuid: currentAccount.card_uuid,
                card_status: currentAccount.card_status,
                cardholderName: currentAccount.cardholderName,
                creditLimit: currentAccount.creditLimit,
                creditLimitParse: currentAccount.creditLimitParse,
                email: currentAccount.email,
                account_uuid: currentAccount.uuid,
                label: currentAccount.tag || '',
                maskedPan: currentAccount.maskedPan.slice(-4),
                requestDate: formatDateExact(currentAccount.requestDate) || "No aplica",
                requesterName: currentAccount.requester?.full_name || "No aplica",
                status: currentAccount.status,
                type: currentAccount.type,
                typeParse: t(`dashboard.card.type.${currentAccount.type}`),
                customer_uuid: currentAccount.master_account_uuid
            });

            setAccountData({
                uuid: currentAccount.uuid,
                profile: {
                    limit: currentAccount.balance?.limit_assigned || 0,
                    spent: currentAccount.balance?.current_balance,
                    disposable: currentAccount.balance?.disposable,
                    currency: 'MXN'
                }
            });

            setViewPermissions({
                can_unblock: currentAccount.card_status === 'enabled' || currentAccount.card_status === 'blocked' && sameUser,
                can_view_nip: currentAccount.card_status === 'enabled' && sameUser,
                can_update_nip: currentAccount.card_status === 'enabled' && sameUser,
                can_cancel: currentAccount.card_status === 'enabled' && !sameUser,
            });

            setAliasValue(currentAccount.cardAlias);
            setSelectedTag(currentAccount.tag);
            setTags(currentAccount?.configuration?.tags || []);
            setIsTemporaryBlocked(currentAccount.card_status === 'blocked');
            setIsBlockedByAdmin(currentAccount.last_activity === 'blocked' && !sameUser);

        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
            resetGuard();
        } finally {
            hide();
        }
    }, [uuid, show, hide, getTransactions, paginationData.currentPage, paginationData.itemsPerPage, handleSetTransactions, checkGuard, resetGuard]);

    const getAccount = useCallback(async () => {
        show();
        setError(null);
        try {
            const currentAccount = accountRef.current;
            const response = await accountService.getAccount(currentAccount.uuid);

            setAliasValue(response.card?.alias);
            setSelectedTag(response.card?.tag);
            setTags(response?.account?.configuration?.tags || []);
            updateAccount((prev: any) => ({
                ...prev,
                cardAlias: response.card?.alias || prev.cardAlias,
                tag: response.card?.tag || prev.tag,
                balance: {
                    ...prev.balance,
                    disposable: response.account?.balance?.disposable ?? prev.balance?.disposable,
                    total_balance_due: response.account?.balance?.total_balance_due ?? prev.balance?.total_balance_due,
                    currency: response.account?.balance?.currency ?? prev.balance?.currency,
                },
                usage_controls: {
                    ...prev.usage_controls,
                    credit_limit: response.account?.balance?.limit_assigned ?? response.account?.limit ?? prev.usage_controls?.credit_limit,
                },
                configuration: {
                    ...prev.configuration,
                    tags: response?.account?.configuration?.tags || prev?.configuration?.tags
                },
                card_status: response.card?.status || prev.status,
                status: response.card?.status || prev.status,
            }));

            setAccountData((prev) => {
                const profile = {
                    limit: response.account?.balance?.limit_assigned || response.account?.limit || 0,
                    spent: response.account?.balance?.total_balance_due || prev?.profile.spent || 0,
                    disposable: response.account?.balance?.disposable || prev?.profile.disposable || 0,
                    currency: response.account?.balance?.currency || prev?.profile.currency || 'MXN',
                };
                if (!prev) return { uuid: response.account?.uuid || '', profile };
                return { ...prev, profile };
            });

            setCard((prev) => {
                if (!prev) return null;
                return {
                    ...prev,
                    cardAlias: response.card?.alias || prev.cardAlias,
                    label: response.card?.tag || prev.label,
                    disposable: response.account?.balance?.disposable ?? prev.disposable,
                    creditLimit: response.account?.balance?.limit_assigned ?? response.account?.limit ?? prev.creditLimit,
                    disposableParse: response.account?.balance?.disposable
                        ? formatMXN(response.account.balance.disposable)
                        : prev.disposableParse,
                    creditLimitParse: response.account?.balance?.limit_assigned ?? response.account?.limit
                        ? formatMXN(response.account.balance.limit_assigned ?? response.account?.limit)
                        : prev.creditLimitParse,
                    card_status: response.card?.status || prev.status,
                    status: response.card?.status || prev.status,
                };
            });

            setIsTemporaryBlocked(response.card?.status === 'blocked');
        } catch (err) {
            setError(t('dashboard.card.error.detail'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide, updateAccount]);

    useEffect(() => {
        if (uuid) {
            fetchCard();
        }
    }, [uuid]);

    const handleEditAlias = useCallback(() => {
        setIsEditAliasModalOpen(true);
    }, []);

    const handleCloseEditAliasModal = useCallback(() => {
        setIsEditAliasModalOpen(false);
    }, []);

    const handleSaveAlias = useCallback(async () => {
        if (!uuid) return;
        show();
        try {
            await metadataUpdateService.updateCardMetadata({ alias: aliasValue }, accountRef.current.id);
            setIsEditAliasModalOpen(false);
            success(t('dashboard.card.detail.success.aliasUpdated'));
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, aliasValue, getAccount, show, hide, success]);

    const handleTagChange = useCallback(async (tag: string) => {
        if (!uuid) return;
        show();
        try {
            await metadataUpdateService.updateCardMetadata({ tag: tag }, accountRef.current.id);
            setSelectedTag(tag);
            success(t('dashboard.card.detail.success.tagChanged'));
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, getAccount, show, hide, success]);

    const handleCreateTag = useCallback(async (name: string) => {
        if (!uuid) return;
        show();

        try {
            await tagManagerService.createTag({ tag_name: name }, authRef.current?.customer_uuid as string, uuid);
            await metadataUpdateService.updateCardMetadata({ tag: name }, accountRef.current.id);
            success(t('dashboard.card.detail.success.tagAdded'));
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, show, hide, success, getAccount]);

    const handleOpenManageTags = useCallback(() => {
        setIsTagMenuOpen(false);
        setIsManageTagsModalOpen(true);
    }, []);

    const handleOpenEditTag = useCallback((name: string) => {
        setEditingTag(name);
        setIsManageTagsModalOpen(false);
        setIsEditTagModalOpen(true);
    }, []);

    const handleUpdateTag = useCallback(async (newName: string) => {
        if (!uuid || !editingTag) return;
        show();
        const checkTag = accountRef.current.tag === editingTag;

        try {
            await tagManagerService.updateTag(
                { tag_name: newName },
                authRef.current?.customer_uuid as string,
                uuid,
                editingTag
            );

            checkTag && await metadataUpdateService.updateCardMetadata(
                { tag: newName },
                accountRef.current.id
            );

            success(t('dashboard.card.detail.modals.updateTag.success'));
            setIsEditTagModalOpen(false);
            setEditingTag(undefined);
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, editingTag, show, hide, success, getAccount]);

    const handleDeleteTag = useCallback(async (tagName: string) => {
        if (!uuid) return;
        show();
        const checkTag = accountRef.current.tag === tagName;

        try {
            await tagManagerService.deleteTag(authRef.current?.customer_uuid as string, uuid, tagName);
            checkTag && accountRef.current.configuration.tags.length > 0 && await metadataUpdateService.updateCardMetadata({ tag: accountRef.current.configuration.tags[0] }, accountRef.current.id);
            success(t('dashboard.card.detail.modals.deleteTag.success'));
            setIsManageTagsModalOpen(false);
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, show, hide, success, getAccount]);

    const handleToggleNipVisibility = useCallback(() => {
        setShowNip((prev) => !prev);
    }, []);

    const handleChangeNip = useCallback(() => {
        setIsChangeNipModalOpen(true);
    }, []);

    const handleCloseChangeNipModal = useCallback(() => {
        setIsChangeNipModalOpen(false);
    }, []);

    const handleToggleBlock = useCallback(async (checked: boolean) => {
        const currentCard = card;
        if (!uuid || !currentCard?.account_uuid || !currentCard?.card_uuid) return;
        show();
        try {
            let payload: any = {
                account: currentCard.account_uuid,
                card_uuid: currentCard.card_uuid,
            }

            if (checked) {
                payload['action'] = 'block';
            }

            await cardService[checked ? 'blockCard' : 'unblockCard'](payload);
            await getAccount();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, show, hide, card, getAccount]);

    const handleEditLabel = useCallback(() => {
        setIsTagMenuOpen(true);
    }, []);

    const handleCancelCard = useCallback(() => {
        const { zendeskUrl } = getEnvConfig();
        const email = card?.email || '';
        const url = new URL(zendeskUrl);
        if (email) {
            url.searchParams.set('email', email);
        }
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
    }, [card?.email]);

    const handleCloseCancelModal = useCallback(() => {
        setIsCancelModalOpen(false);
    }, []);

    const handleConfirmCancel = useCallback(async () => {
        if (!uuid) return;
        show();
        try {
            await cardService.cancelCard({ uuid });
            setIsCancelModalOpen(false);
            resetGuard();
            await fetchCard();
        } catch (err) {
            console.error(err);
        } finally {
            hide();
        }
    }, [uuid, fetchCard, show, hide, resetGuard]);

    const handleReportTheft = useCallback(() => {
        const { zendeskUrl } = getEnvConfig();
        const email = card?.email || '';
        const url = new URL(zendeskUrl);
        if (email) {
            url.searchParams.set('email', email);
        }
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
    }, [card?.email]);

    const handleManageLimit = useCallback(() => {
        setIsManageLimitModalOpen(true);
    }, []);

    const handleCloseManageLimitModal = useCallback(() => {
        setIsManageLimitModalOpen(false);
    }, []);

    const handlePageChange = useCallback((page: number, limit: number) => {
        setPaginationData((prev) => ({
            ...prev,
            currentPage: page,
            itemsPerPage: limit,
        }));
        getTransactions(page, limit, true);
    }, [getTransactions]);

    const pagination = {
        ...paginationData,
        onPageChange: handlePageChange
    };

    return {
        card,
        activityData,
        accountData: { account: accountData, view: viewPermissions },
        error,
        refetch: () => {
            resetGuard();
            return fetchCard();
        },
        onGetAccount: getAccount,
        state: {
            isTemporaryBlocked,
            isBlockedByAdmin,
            showNip,
            isChangeNipModalOpen,
            isEditAliasModalOpen,
            isTagMenuOpen,
            isCreateTagModalOpen,
            isManageTagsModalOpen,
            isEditTagModalOpen,
            aliasValue,
            isManageLimitModalOpen,
            isCancelModalOpen,
            tags: tags.map((t) => ({ value: t, label: t })),
            selectedTag,
            nipValue: nipValue,
            editingTag,
        },
        controls: {
            setIsTagMenuOpen,
            setIsCreateTagModalOpen,
            setIsManageTagsModalOpen,
            setIsEditTagModalOpen,
            setAliasValue,
            tagButtonRef,
        },
        handlers: {
            handleEditAlias,
            handleCloseEditAliasModal,
            handleSaveAlias,
            handleTagChange,
            handleCreateTag,
            handleUpdateTag,
            handleDeleteTag,
            handleOpenManageTags,
            handleOpenEditTag,
            handleToggleNipVisibility,
            handleChangeNip,
            handleCloseChangeNipModal,
            handleToggleBlock,
            handleEditLabel,
            handleCancelCard,
            handleCloseCancelModal,
            handleConfirmCancel,
            handleReportTheft,
            handleManageLimit,
            handleCloseManageLimitModal,
        },
        pagination,
    };
};
